import './Donation.scss'

import {BlockType, Blocks} from '../blocks/Blocks'
import {FieldTypes, FormStepType} from '../blocks/FormBlock'
import {Hero, HeroType} from '../layout/Hero'
import {InternalLinkType, PageData} from '../../types'
import React, {useState} from 'react'

import {Breadcrumbs} from '../layout/Breadcrumbs'
import {Layout} from '../layout/Layout'
import {Text} from '../blocks/form'
import {Theme} from '../Theme'
import amountField from '../evenementen/layout/EditionRegistration/fields/amountField'
import box from '../webshop/pages/cart/orderform/fields/box'
import city from '../webshop/pages/cart/orderform/fields/city'
import emailField from '../evenementen/layout/EditionRegistration/fields/emailField'
import firstNameField from '../evenementen/layout/EditionRegistration/fields/firstNameField'
import {formatBelgianNationalNumberPartial} from '../../util/formatBelgianNationalNumberPartial'
import houseNumber from '../webshop/pages/cart/orderform/fields/houseNumber'
import lastNameField from '../evenementen/layout/EditionRegistration/fields/lastNameField'
import {number_format} from '../../util/price'
import optin from '../webshop/pages/cart/orderform/fields/optin'
import phoneField from '../evenementen/layout/MemberRegistration/fields/phoneField'
import streetField from '../evenementen/layout/EditionRegistration/fields/streetField'
import {style} from '../../util/style'
import {useTranslation} from '../../util/i18n'
import {wrap} from '@codeurs/front'
import zip from '../webshop/pages/cart/orderform/fields/zip'

const styles = style('donation', ['form', 'advantage', 'messagecounter'])

type DonationFormType = {
	reCaptchaKey: string
	amount: number
	source: string
	team: {
		id: number | null
		name: string | null
	}
	action: {
		id: number | null
		name: string | null
	}
	form_privacy: InternalLinkType
}

export type DonationType = {
	hero: HeroType
	blocks?: Array<BlockType>
} & DonationFormType

export const Donation: React.FC<PageData & DonationType> = wrap(Layout)(
	({...props}) => {
		const {
			action,
			parents,
			title,
			hero,
			blocks,
			reCaptchaKey,
			amount,
			source,
			team,
			form_privacy
		} = props

		return (
			<div className={styles()}>
				<Hero {...hero} title={(hero && hero.title) || title} />
				<Breadcrumbs parents={parents} title={title} />
				<Blocks blocks={blocks} container={true} />
				<DonationForm
					action={action}
					reCaptchaKey={reCaptchaKey}
					amount={amount}
					source={source}
					team={team}
					form_privacy={form_privacy}
				/>
			</div>
		)
	}
)

const removeAllButLast = (text: string, delimiter: string): string => {
	const parts = text.split(delimiter)
	return (
		parts.slice(0, -1).join('') +
		(parts.length > 1 ? delimiter : '') +
		parts.slice(-1)
	)
}

export const formatNumber = (text: string): number =>
	(removeAllButLast(
		text.replace(/[^0-9.,]/g, '').replace(',', '.'),
		'.'
	) as unknown) as number

const Advantage: React.FC<{amount: number}> = ({amount}) => {
	const t = useTranslation('donation')
	const advantage: number =
		amount < 40
			? 0
			: (parseInt(((amount * 0.45 * 100) as unknown) as string, 10) as number) /
			  100
	return (
		<div className={styles.advantage()}>
			{t.fiscalAdvantage} = €{number_format(advantage, 2, ',', '.')}{' '}
			<span className={styles.advantage('devider')}>/</span>{' '}
			<span className={styles.advantage('costs')}>
				{t.finalCost} = <b>€{number_format(amount - advantage, 2, ',', '.')}</b>
			</span>
			<span id="total-amount" class={styles.advantage('amount')}>
				{amount}
			</span>
		</div>
	)
}

const maxMessageLength: number = 120
const MessageCounter: React.FC<{messageLength: number}> = ({messageLength}) => {
	const t = useTranslation('donation')
	return (
		<Text
			text={t.remainingCharacters.replace(
				'{{remainingCharacters}}',
				`${maxMessageLength - messageLength}`
			)}
		/>
	)
}

const getSection5Fields = (
	team: {
		id: number | null
		name: string | null
	},
	action: {
		id: number | null
		name: string | null
	},
	formPrivacy: InternalLinkType
): FieldTypes => {
	const t = useTranslation('donation')
	const fields: FieldTypes = []
	if (team.id || action.id) {
		fields.push({
			id: 1,
			parent_id: 5,
			channel: 'checkbox',
			label: team.id ? t.showOnTeamPage : t.showOnActionPage,
			name: 'showOnTeamPage',
			required: false
		})
		fields.push({
			id: 2,
			parent_id: 5,
			channel: 'checkbox',
			label: team.id ? t.teamCanKnow : t.actionCanKnow,
			name: 'teamCanKnow',
			required: false
		})
	}

	fields.push({
		id: 3,
		parent_id: 5,
		channel: 'checkbox',
		label: `${t.privacy} <a href="${
			formPrivacy && formPrivacy.url
		}" target="_blank">${
			formPrivacy
				? formPrivacy.description
					? formPrivacy.description
					: formPrivacy.title
				: ''
		}</a>`,
		name: 'privacy',
		required: true
	})

	return fields
}

const getSteps = (
	team: {
		id: number | null
		name: string | null
	},
	action: {
		id: number | null
		name: string | null
	},
	formPrivacy: InternalLinkType,
	price: number,
	message: string,
	showRrn: boolean
): Array<FormStepType> => {
	const t = useTranslation('donation')
	const isTeam: boolean = team.id && team.name !== ''
	const isAction: boolean = action.id && action.name !== ''
	return [
		{
			id: 1,
			parent_id: 0,
			channel: 'step',
			label: '',
			submit_text: t.submit,
			sections: [
				{
					id: 1,
					parent_id: 1,
					channel: 'section',
					label: isTeam
						? t.supportTeam.replace('{{teamName}}', team.name)
						: isAction
						? t.supportTeam.replace('{{teamName}}', action.name)
						: t.supportUs,
					fields: [
						<Text key="info" text={t.info} />,
						{...firstNameField, id: 1, parent_id: 1},
						{...lastNameField, id: 2, parent_id: 1},
						{...amountField, id: 3, parent_id: 1},
						<div key="advantage" className={'form_field mod-half'}>
							<Advantage amount={price} />
						</div>,
						!isTeam && !isAction && <Text key="info" text={t.infoExtra} />
					]
				},
				{
					id: 2,
					parent_id: 1,
					channel: 'section',
					label: '',
					fields: [
						{...streetField, id: 1, parent_id: 2},
						{...houseNumber, id: 2, parent_id: 2},
						{...box, id: 3, parent_id: 2},
						{...zip, id: 4, parent_id: 2},
						{...city, id: 5, parent_id: 2},
						{...emailField, id: 6, parent_id: 2, mod: 'half'},
						{...phoneField, id: 7, parent_id: 2, required: false},
						{...optin, id: 8, parent_id: 2, required: false, label: t.optin}
					]
				},
				{
					id: 3,
					parent_id: 1,
					channel: 'section',
					label: '',
					fields: [
						{
							id: 1,
							parent_id: 3,
							channel: 'input',
							name: 'boodschap',
							label: isTeam
								? `${t.messageTo} ${team.name}`
								: isAction
								? `${t.messageTo} ${action.name}`
								: `${t.messageTo} Broederlijk Delen`,
							required: false,
							type: 'textarea',
							placeholder: ''
						},
						<MessageCounter
							key="messageCounter"
							messageLength={message.length}
						/>
					]
				},
				{
					id: 4,
					parent_id: 1,
					channel: 'section',
					label: '',
					fields: [
						<Text
							key="gift_info"
							text={isAction ? t.actionInfo : t.giftinfo}
						/>,
						{
							id: 1,
							parent_id: 4,
							channel: 'radio',
							name: 'type',
							label: '',
							required: true,
							options: [
								{
									path: 'gift',
									title: isAction ? t.actionGiftOption : t.giftOption
								},
								{
									path: 'collection',
									title: isAction ? t.actionYieldOption : t.yieldOption
								}
							]
						},
						showRrn ? (
							<Text
								key="rrninfo"
								text={
									'Wenst u graag een <b>fiscaal attest</b>? De FOD Financiën verplicht ons om vanaf 2024 uw rijksregisternummer mee te sturen met uw giften. Zonder rijksregisternummer zal u geen fiscaal attest meer ontvangen.'
								}
							/>
						) : null,
						showRrn ? rrn : null
					]
				},
				{
					id: 5,
					parent_id: 1,
					channel: 'section',
					label: '',
					fields: getSection5Fields(team, action, formPrivacy)
				}
			]
		}
	]
}

const rrn = {
	id: 2,
	parent_id: 4,
	channel: 'input',
	name: 'rrn',
	label: 'Rijksregisternummer',
	required: false,
	type: 'text',
	placeholder: '',
	pattern: '[0-9]{2}.[0-9]{2}.[0-9]{2}-[0-9]{3}.[0-9]{2}',
	minLength: 15,
	maxLength: 15
}

const DonationForm: React.FC<DonationFormType> = ({
	action,
	reCaptchaKey,
	amount,
	source,
	team,
	form_privacy
}) => {
	const [data, setData] = useState<{[key: string]: any}>({
		'1-1-3': amount,
		source,
		teamEntryId: team.id,
		actionEntryId: action.id
	})
	const [price, setPrice] = useState<number>(formatNumber(`${amount}`))
	const [message, setMessage] = useState<string>('')
	const [showRrn, setShowRrn] = useState<boolean>(false)
	const steps: Array<FormStepType> = getSteps(
		team,
		action,
		form_privacy,
		price,
		message,
		showRrn
	)

	const onChangeHandler = (key, value) => {
		switch (key) {
			case '1-1-3':
				const newPrice: number = formatNumber(value)
				setPrice(newPrice)
				setData({...data, '1-1-3': newPrice})
				break
			case '1-3-1':
				const newMessage =
					value.length < maxMessageLength
						? value
						: (value as string).substr(0, maxMessageLength)
				setMessage(newMessage)
				setData({...data, '1-3-1': newMessage})
				break
			case '1-4-1':
				if (value === 'gift') {
					setShowRrn(true)
				} else {
					setShowRrn(false)
					setData({...data, '1-4-2': ''})
				}
				break
			case '1-4-2':
				setData({
					...data,
					'1-4-2': formatBelgianNationalNumberPartial(value)
				})
				break
		}
	}
	const onSubmitHandler = (response) => {
		if (response && response.checkoutUrl)
			window.location.href = response.checkoutUrl
	}
	return (
		<div className={styles.form()}>
			<Theme.Container>
				<Blocks
					blocks={[
						{
							type: 'form' as 'form',
							path: '',
							requestUri: window.location.pathname,
							reCaptchaKey,
							id: 1,
							pageId: 0,
							steps,
							data,
							onChange: onChangeHandler,
							useEffect: true,
							onSubmit: onSubmitHandler
						}
					]}
				/>
			</Theme.Container>
		</div>
	)
}
